import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchRoute } from "../../../api/apiFetchRoute";
import { toUpdateAccount } from "../../../api/user";
import { EnumUserType } from "../../../generated/enums";
import { Account } from "../../../generated/types";
import { formatDate } from "../../../utils/formatDate";
import { Button } from "../../common/Button";
import { DisplayValue } from "../../common/DisplayValue";
import { FormInput } from "../../common/FormLayout";
import { LabelParam } from "../../common/LabelParam";
import { sectionToIcon } from "../../type/default";
import { UserTypeBadge } from "./UserTypeBadge";

export const UserSection = ({
  user,
  detailLevel = "default",
  onClick,
  onUpdate,
}: {
  user: Account;
  detailLevel?: "default" | "mini" | "maxi";
  onClick?: () => void;
  onUpdate?: () => void;
}) => {
  const { t } = useTranslation();
  // Affiche l'enregistrement en attendant que le parent renvoie les données à jour.
  const [savedUser, setSavedUser] = useState<Account | null>(null);
  const theUser = savedUser ?? user;
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(user);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    setSavedUser(null);
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setSaveFailed(false);
    try {
      const response = await apiFetchRoute(
        "putAccounts",
        toUpdateAccount(editedUser)
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      setIsEditing(false);
      setSavedUser(editedUser);
      onUpdate?.();
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      setSaveFailed(true);
    }
  };

  const startEditing = () => {
    setEditedUser(theUser);
    setSaveFailed(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setSaveFailed(false);
    setIsEditing(false);
  };

  return (
    <div>
      <div className="flex justify-between border-b border-grey-light mb-4">
        <div className="flex gap-4 items-center">
          {detailLevel === "maxi" && (
            <FontAwesomeIcon icon={sectionToIcon.section_account} />
          )}
          <button
            className={`${detailLevel === "mini" ? "text-sm" : detailLevel === "maxi" ? "text-lg" : "text-base"} font-oxaniumMedium ${
              onClick ? "hover:text-tertiary cursor-pointer" : "cursor-default"
            }`}
            onClick={onClick}
          >
            {theUser.first_name + " " + theUser.last_name}
          </button>
        </div>
        {detailLevel === "maxi" && !isEditing && (
          <button
            onClick={startEditing}
            className="text-grey-medium hover:text-secondary"
          >
            <FontAwesomeIcon icon="pen-to-square" className="h-5 w-5" />
          </button>
        )}
      </div>

      {isEditing && detailLevel !== "mini" ? (
        <div className="space-y-2">
          <LabelParam label={t("user-name")}>
            <FormInput
              value={editedUser.last_name}
              onChange={handleChange}
              name="last_name"
              sizeInput="small"
              className="bg-transparent border-none p-0 text-sm"
            />
          </LabelParam>

          <LabelParam label={t("user-firstname")}>
            <FormInput
              value={editedUser.first_name}
              onChange={handleChange}
              name="first_name"
              sizeInput="small"
              className="bg-transparent border-none p-0 text-sm"
            />
          </LabelParam>

          <LabelParam label={t("user-type")}>
            <UserTypeBadge type={theUser.type as EnumUserType} />
          </LabelParam>

          {detailLevel === "maxi" && (
            <div className="space-y-2">
              {/* Le back retrouve le compte par son email : il ne se modifie pas ici. */}
              <LabelParam label={t("user-email")}>
                <DisplayValue value={editedUser.email} />
              </LabelParam>

              <LabelParam label={t("user-phone-mobile")}>
                <FormInput
                  value={editedUser.phone_mobile}
                  onChange={handleChange}
                  name="phone_mobile"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>
              <LabelParam label={t("user-phone-fixe")}>
                <FormInput
                  value={editedUser.phone_fixe}
                  onChange={handleChange}
                  name="phone_fixe"
                  sizeInput="small"
                  className="bg-transparent border-none p-0 text-sm"
                />
              </LabelParam>

              <LabelParam label={t("user-creation-date")}>
                <DisplayValue value={formatDate(editedUser.creation_date)} />
              </LabelParam>
            </div>
          )}

          {saveFailed && (
            <p className="text-xs text-warning-readable">
              {t("ticket-update-error")}
            </p>
          )}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="secondary" onClick={handleCancel}>
              {t("button-cancel")}
            </Button>
            <Button type="submit" onClick={handleSubmit}>
              {t("button-save")}
            </Button>
          </div>
        </div>
      ) : (
        detailLevel !== "mini" && (
          <div className="space-y-4">
            <LabelParam label={t("user-name")}>
              <DisplayValue value={theUser.last_name} />
            </LabelParam>

            <LabelParam label={t("user-firstname")}>
              <DisplayValue value={theUser.first_name} />
            </LabelParam>

            <LabelParam label={t("user-type")}>
              <UserTypeBadge type={theUser.type as EnumUserType} />
            </LabelParam>

            {detailLevel === "maxi" && (
              <div className="space-y-4">
                <LabelParam label={t("user-email")}>
                  <DisplayValue value={theUser.email} />
                </LabelParam>

                {theUser.phone_mobile && (
                  <LabelParam label={t("user-phone-mobile")}>
                    <DisplayValue value={theUser.phone_mobile} />
                  </LabelParam>
                )}

                {theUser.phone_fixe && (
                  <LabelParam label={t("user-phone-fixe")}>
                    <DisplayValue value={theUser.phone_fixe} />
                  </LabelParam>
                )}

                <LabelParam label={t("user-creation-date")}>
                  <DisplayValue value={formatDate(theUser.creation_date)} />
                </LabelParam>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
};
